import { useRef, useState, type CSSProperties, type PointerEvent, type WheelEvent } from 'react';
import type { GraphPoint, GraphSeries } from '../../lib/decode/graphSeries';
import type { EventMarker } from '../../lib/decode/eventMarkers';

const BYTE_COLORS = ['#2dd4ff', '#6ee7b7', '#f7c873', '#ff8a8a', '#a78bfa', '#60a5fa', '#f472b6', '#c4f04c'];
const CHART_WIDTH = 900;
const CHART_HEIGHT = 280;
const PADDING = { top: 18, right: 18, bottom: 34, left: 42 };

type ByteSeriesChartProps = {
  series: GraphSeries | null;
  enabledBytes: boolean[];
  eventMarkers?: EventMarker[];
  eventWindowMs?: number;
  focusedByteIndex?: number | null;
  focusedTimestampMs?: number | null;
  overlays?: ChartOverlaySeries[];
  onZoomUsed?: () => void;
};

export type ChartOverlaySeries = {
  id: string;
  label: string;
  color: string;
  points: Array<{ t: number; value: number }>;
  dashed?: boolean;
  unit?: string;
};

function formatX(value: number, xMode: GraphSeries['xMode']) {
  if (xMode === 'index') return value.toFixed(0);
  if (value >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${value.toFixed(0)}ms`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function nearestPoint(points: GraphPoint[], targetX: number) {
  if (points.length === 0) return null;
  let closest = points[0];
  let closestDelta = Math.abs(points[0].t - targetX);
  for (let index = 1; index < points.length; index += 1) {
    const delta = Math.abs(points[index].t - targetX);
    if (delta < closestDelta) {
      closest = points[index];
      closestDelta = delta;
    }
  }
  return closest;
}

function nearestOverlayPoint(points: Array<{ t: number; value: number }>, targetX: number) {
  if (points.length === 0) return null;
  let closest = points[0];
  let closestDelta = Math.abs(points[0].t - targetX);
  for (let index = 1; index < points.length; index += 1) {
    const delta = Math.abs(points[index].t - targetX);
    if (delta < closestDelta) {
      closest = points[index];
      closestDelta = delta;
    }
  }
  return closest;
}

function makeViewPath(points: GraphPoint[], byteIndex: number, xMin: number, xMax: number, yMin: number, yMax: number) {
  const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const xSpan = Math.max(1, xMax - xMin);
  const ySpan = Math.max(1, yMax - yMin);
  const commands: string[] = [];
  let segmentOpen = false;

  for (const point of points) {
    const value = point.bytes[byteIndex];
    if (value === null) {
      segmentOpen = false;
      continue;
    }

    const x = PADDING.left + ((point.t - xMin) / xSpan) * plotWidth;
    const y = PADDING.top + (1 - (value - yMin) / ySpan) * plotHeight;
    commands.push(`${segmentOpen ? 'L' : 'M'} ${x.toFixed(2)} ${y.toFixed(2)}`);
    segmentOpen = true;
  }

  return commands.join(' ');
}

function makeOverlayPath(points: Array<{ t: number; value: number }>, xMin: number, xMax: number) {
  const viewPoints: Array<{ t: number; value: number }> = [];
  let minValue: number | null = null;
  let maxValue: number | null = null;

  for (const point of points) {
    if (!Number.isFinite(point.t) || !Number.isFinite(point.value) || point.t < xMin || point.t > xMax) continue;
    viewPoints.push(point);
    minValue = minValue === null || point.value < minValue ? point.value : minValue;
    maxValue = maxValue === null || point.value > maxValue ? point.value : maxValue;
  }

  if (viewPoints.length < 2) return '';
  const padding = Math.max(0.0001, ((maxValue ?? 0) - (minValue ?? 0)) * 0.12);
  const yMin = (minValue ?? 0) - padding;
  const yMax = (maxValue ?? 0) + padding;
  const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const xSpan = Math.max(1, xMax - xMin);
  const ySpan = Math.max(0.0001, yMax - yMin);
  return viewPoints.map((point, index) => {
    const x = PADDING.left + ((point.t - xMin) / xSpan) * plotWidth;
    const y = PADDING.top + (1 - (point.value - yMin) / ySpan) * plotHeight;
    return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');
}

export function ByteSeriesChart({
  series,
  enabledBytes,
  eventMarkers = [],
  eventWindowMs = 100,
  focusedByteIndex = null,
  focusedTimestampMs = null,
  overlays = [],
  onZoomUsed
}: ByteSeriesChartProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hoverPoint, setHoverPoint] = useState<GraphPoint | null>(null);
  const [viewRange, setViewRange] = useState<{ min: number; max: number } | null>(null);
  const [dragStart, setDragStart] = useState<{ clientX: number; range: { min: number; max: number } } | null>(null);

  if (!series) {
    return (
      <div className="byte-chart-empty">
        <h3>No graphable data for selected CAN ID.</h3>
        <p>Select a CAN ID with parsed frames to inspect byte activity over time.</p>
      </div>
    );
  }

  if (series.points.length < 2) {
    return (
      <div className="byte-chart-empty">
        <h3>More frames needed</h3>
        <p>This CAN ID has fewer than 2 frames, so there is not enough data for a time-series graph.</p>
      </div>
    );
  }

  let fullMinX = series.points[0].t;
  let fullMaxX = series.points[0].t;
  for (const point of series.points) {
    if (point.t < fullMinX) fullMinX = point.t;
    if (point.t > fullMaxX) fullMaxX = point.t;
  }
  const currentRange = viewRange && viewRange.max > viewRange.min
    ? {
      min: clamp(viewRange.min, fullMinX, Math.max(fullMinX, fullMaxX - 1)),
      max: clamp(viewRange.max, Math.min(fullMaxX, fullMinX + 1), fullMaxX)
    }
    : { min: fullMinX, max: fullMaxX };
  const minX = currentRange.min;
  const maxX = currentRange.max;
  const midX = minX + (maxX - minX) / 2;
  const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const viewPoints = series.points.filter((point) => point.t >= minX && point.t <= maxX);
  let rawYMin: number | null = null;
  let rawYMax: number | null = null;
  for (const point of viewPoints) {
    point.bytes.forEach((value, index) => {
      if (!enabledBytes[index] || value === null) return;
      rawYMin = rawYMin === null || value < rawYMin ? value : rawYMin;
      rawYMax = rawYMax === null || value > rawYMax ? value : rawYMax;
    });
  }
  rawYMin = rawYMin ?? 0;
  rawYMax = rawYMax ?? 255;
  const yPadding = Math.max(4, (rawYMax - rawYMin) * 0.12);
  const yMin = clamp(Math.floor(rawYMin - yPadding), 0, 255);
  const yMax = clamp(Math.ceil(rawYMax + yPadding), Math.min(255, yMin + 1), 255);
  const hoverX = hoverPoint
    ? PADDING.left + ((hoverPoint.t - minX) / Math.max(1, maxX - minX)) * plotWidth
    : null;
  const hoverOverlayValues = hoverPoint
    ? overlays.flatMap((overlay) => {
      const nearest = nearestOverlayPoint(overlay.points, hoverPoint.t);
      return !nearest || !Number.isFinite(nearest.value)
        ? []
        : [{ ...overlay, value: nearest.value }];
    })
    : [];
  const focusedPoint = focusedTimestampMs === null || focusedTimestampMs < minX || focusedTimestampMs > maxX
    ? null
    : nearestPoint(viewPoints, focusedTimestampMs);
  const focusedPointX = focusedPoint
    ? PADDING.left + ((focusedPoint.t - minX) / Math.max(1, maxX - minX)) * plotWidth
    : null;
  const focusedPointValue = focusedPoint && focusedByteIndex !== null ? focusedPoint.bytes[focusedByteIndex] : null;
  const focusedPointY = focusedPointValue === null
    ? null
    : PADDING.top + (1 - (focusedPointValue - yMin) / Math.max(1, yMax - yMin)) * plotHeight;

  function clientToChartX(clientX: number) {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const relativeX = clamp((clientX - rect.left) / rect.width, 0, 1);
    const chartX = relativeX * CHART_WIDTH;
    const plotRatio = clamp((chartX - PADDING.left) / plotWidth, 0, 1);
    return minX + plotRatio * (maxX - minX);
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    const chartX = clientToChartX(event.clientX);
    if (chartX === null) return;
    if (dragStart) {
      if (fullMaxX <= fullMinX) return;
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const deltaPixels = event.clientX - dragStart.clientX;
      const deltaX = (deltaPixels / rect.width) * (dragStart.range.max - dragStart.range.min);
      const span = dragStart.range.max - dragStart.range.min;
      const nextMin = clamp(dragStart.range.min - deltaX, fullMinX, fullMaxX - span);
      setViewRange({ min: nextMin, max: nextMin + span });
      return;
    }
    setHoverPoint(nearestPoint(viewPoints, chartX));
  }

  function handleWheel(event: WheelEvent<HTMLDivElement>) {
    event.preventDefault();
    if (fullMaxX <= fullMinX) return;
    const chartX = clientToChartX(event.clientX);
    if (chartX === null) return;
    const span = maxX - minX;
    const minSpan = Math.max(1, (fullMaxX - fullMinX) / 80);
    const factor = event.deltaY < 0 ? 0.78 : 1.28;
    const nextSpan = clamp(span * factor, minSpan, Math.max(1, fullMaxX - fullMinX));
    const anchorRatio = (chartX - minX) / Math.max(1, span);
    let nextMin = chartX - nextSpan * anchorRatio;
    nextMin = clamp(nextMin, fullMinX, fullMaxX - nextSpan);
    setViewRange({ min: nextMin, max: nextMin + nextSpan });
    onZoomUsed?.();
  }

  function resetZoom() {
    setViewRange(null);
    setHoverPoint(null);
  }

  return (
    <div
      className="byte-chart-wrap interactive"
      ref={wrapRef}
      role="img"
      aria-label={`Interactive byte activity chart for ${series.canId}`}
      onDoubleClick={resetZoom}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragStart({ clientX: event.clientX, range: currentRange });
      }}
      onPointerLeave={() => {
        if (!dragStart) setHoverPoint(null);
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragStart(null);
      }}
      onWheel={handleWheel}
    >
      <svg className="byte-chart" viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} preserveAspectRatio="none">
        <rect className="byte-chart-bg" x="0" y="0" width={CHART_WIDTH} height={CHART_HEIGHT} rx="10" />
        {(series.xMode === 'timestamp' ? eventMarkers : []).map((marker) => {
          const start = marker.timestampMs - eventWindowMs;
          const end = marker.timestampMs + eventWindowMs;
          if (end < minX || start > maxX) return null;
          const x1 = PADDING.left + ((Math.max(start, minX) - minX) / Math.max(1, maxX - minX)) * plotWidth;
          const x2 = PADDING.left + ((Math.min(end, maxX) - minX) / Math.max(1, maxX - minX)) * plotWidth;
          return (
            <g key={marker.id}>
              <rect className="byte-chart-event-band" x={x1} y={PADDING.top} width={Math.max(2, x2 - x1)} height={plotHeight} />
              <text className="byte-chart-event-label" x={x1 + 4} y={PADDING.top + 14}>{marker.eventType}</text>
            </g>
          );
        })}
        {[yMin, yMin + (yMax - yMin) / 2, yMax].map((value) => {
          const y = PADDING.top + (1 - (value - yMin) / Math.max(1, yMax - yMin)) * plotHeight;
          return (
            <g key={value}>
              <line className="byte-chart-grid" x1={PADDING.left} x2={CHART_WIDTH - PADDING.right} y1={y} y2={y} />
              <text className="byte-chart-axis" x={PADDING.left - 10} y={y + 4} textAnchor="end">{value.toFixed(0)}</text>
            </g>
          );
        })}
        <line className="byte-chart-axis-line" x1={PADDING.left} x2={CHART_WIDTH - PADDING.right} y1={CHART_HEIGHT - PADDING.bottom} y2={CHART_HEIGHT - PADDING.bottom} />
        <line className="byte-chart-axis-line" x1={PADDING.left} x2={PADDING.left} y1={PADDING.top} y2={CHART_HEIGHT - PADDING.bottom} />
        {Array.from({ length: 8 }, (_, byteIndex) => (
          <path
            className={`byte-chart-line ${focusedByteIndex === byteIndex ? 'focused' : ''} ${!enabledBytes[byteIndex] ? 'hidden' : ''}`}
            d={enabledBytes[byteIndex] ? makeViewPath(viewPoints, byteIndex, minX, maxX, yMin, yMax) : ''}
            key={byteIndex}
            stroke={BYTE_COLORS[byteIndex]}
          />
        ))}
        {overlays.map((overlay) => (
          <path
            className={`byte-chart-overlay-line ${overlay.dashed ? 'dashed' : ''}`}
            d={makeOverlayPath(overlay.points, minX, maxX)}
            key={overlay.id}
            stroke={overlay.color}
          />
        ))}
        {focusedPointX !== null && focusedPointY !== null && (
          <g>
            <line className="byte-chart-focus-line" x1={focusedPointX} x2={focusedPointX} y1={PADDING.top} y2={CHART_HEIGHT - PADDING.bottom} />
            <circle className="byte-chart-focus-point" cx={focusedPointX} cy={focusedPointY} r="5" fill={BYTE_COLORS[focusedByteIndex ?? 0]} />
          </g>
        )}
        {hoverPoint && hoverX !== null && (
          <line className="byte-chart-cursor" x1={hoverX} x2={hoverX} y1={PADDING.top} y2={CHART_HEIGHT - PADDING.bottom} />
        )}
        {[
          [minX, 'start'],
          [midX, 'middle'],
          [maxX, 'end']
        ].map(([value, anchor]) => {
          const x = PADDING.left + ((Number(value) - minX) / Math.max(1, maxX - minX)) * (CHART_WIDTH - PADDING.left - PADDING.right);
          return (
            <text className="byte-chart-axis" key={`${value}-${anchor}`} x={x} y={CHART_HEIGHT - 10} textAnchor={anchor as 'start' | 'middle' | 'end'}>
              {formatX(Number(value), series.xMode)}
            </text>
          );
        })}
      </svg>
      {hoverPoint && (
        <div className="byte-chart-tooltip" style={{ left: `${clamp(((hoverX ?? PADDING.left) / CHART_WIDTH) * 100, 8, 82)}%` }}>
          <strong>{formatX(hoverPoint.t, series.xMode)}</strong>
          <div>
            {hoverPoint.bytes.map((value, byteIndex) => (
              enabledBytes[byteIndex] ? (
                <span key={byteIndex} style={{ '--byte-color': BYTE_COLORS[byteIndex] } as CSSProperties}>
                  B{byteIndex}: {value === null ? '--' : value}
                </span>
              ) : null
            ))}
            {hoverOverlayValues.map((overlay) => (
              <span key={overlay.id} style={{ '--byte-color': overlay.color } as CSSProperties}>
                {overlay.label}: {overlay.value.toFixed(2)}{overlay.unit ? ` ${overlay.unit}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="byte-chart-instructions">
        <span>Hover for values</span>
        <span>Wheel zoom</span>
        <span>Drag pan</span>
        <span>Double-click reset</span>
      </div>
    </div>
  );
}

export { BYTE_COLORS };
